import type { LogicalType } from '@/types'
import type { DataTypeDefinition, DialectAdapter, DialectWarning } from '../dialectTypes'

const DATA_TYPES: DataTypeDefinition[] = [
  { value: 'INTEGER',      label: 'INTEGER',          logicalType: 'integer' },
  { value: 'REAL',         label: 'REAL',             logicalType: 'float' },
  { value: 'TEXT',         label: 'TEXT',             logicalType: 'text' },
  { value: 'NUMERIC(10,2)',label: 'NUMERIC(p,s)',     logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'DECIMAL(10,2)',label: 'DECIMAL(p,s)',     logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'BLOB',         label: 'BLOB',             logicalType: 'binary' },
  // SQLite uses type affinity; these map to one of the 5 storage classes
  { value: 'TEXT',         label: 'TEXT (string)',    logicalType: 'string' },
  { value: 'INTEGER',      label: 'INTEGER (bigint)', logicalType: 'bigint' },
  { value: 'INTEGER',      label: 'INTEGER (bool)',   logicalType: 'boolean' },
  { value: 'TEXT',         label: 'TEXT (date)',      logicalType: 'date' },
  { value: 'TEXT',         label: 'TEXT (datetime)',  logicalType: 'datetime' },
  { value: 'TEXT',         label: 'TEXT (uuid)',      logicalType: 'uuid' },
  { value: 'TEXT',         label: 'TEXT (json)',      logicalType: 'json' },
]

const LOGICAL_TO_PHYSICAL: Record<LogicalType, string> = {
  string:   'TEXT',
  text:     'TEXT',
  integer:  'INTEGER',
  bigint:   'INTEGER',
  smallint: 'INTEGER',
  decimal:  'NUMERIC',
  float:    'REAL',
  boolean:  'INTEGER',
  date:     'TEXT',
  time:     'TEXT',
  datetime: 'TEXT',
  uuid:     'TEXT',
  json:     'TEXT',
  binary:   'BLOB',
}

export const sqliteDialect: DialectAdapter = {
  id: 'sqlite',
  label: 'SQLite',
  dataTypes: DATA_TYPES,

  mapLogicalToPhysical(type) {
    return LOGICAL_TO_PHYSICAL[type] ?? 'TEXT'
  },

  mapPhysicalToLogical(type) {
    const key = type.trim().toUpperCase().replace(/\s*\(.*\)/, '').replace(/\s+/g, ' ')
    switch (key) {
      case 'INTEGER':
      case 'INT':
        return 'integer'
      case 'REAL':
      case 'FLOAT':
      case 'DOUBLE':
        return 'float'
      case 'NUMERIC':
      case 'DECIMAL':
        return 'decimal'
      case 'BLOB':
        return 'binary'
      default:
        return 'text'
    }
  },

  validateColumn(column) {
    const warnings: DialectWarning[] = []
    const upper = column.type.trim().toUpperCase()
    if (upper === 'JSONB') {
      warnings.push({ column: column.name, message: 'JSONB is not supported in SQLite; use TEXT to store JSON.', severity: 'warning' })
    }
    if (upper === 'UUID') {
      warnings.push({ column: column.name, message: 'UUID is not a native SQLite type; use TEXT.', severity: 'info' })
    }
    return warnings
  },
}
